import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useGpComplianceAuditLogQuery } from "../../../graphql/supergraphHooks";
import { isNoEntityAccessError } from "../utils";
import { filterAndTransformGpAuditLogEvents } from "./transformers";
import type { GpComplianceAuditLogEvent } from "./types";
import type { ComplianceCaseTypeEnum } from "../../../graphql/supergraphTypes";

export const useComplianceAuditLog = ({
  arenaId,
  arenaName,
  entityIds,
  positionId,
  caseTypes,
  dateFrom,
  dateTo,
  limit = 10,
}: {
  arenaId: number;
  arenaName?: string;
  entityIds?: number[];
  positionId?: number;
  caseTypes?: ComplianceCaseTypeEnum[];
  dateFrom?: string;
  dateTo?: string;
  limit?: number;
}) => {
  const [offset, setOffset] = useState(0);
  const [allRawEvents, setAllRawEvents] = useState<GpComplianceAuditLogEvent[]>([]);

  const filterKey = [
    arenaId,
    JSON.stringify(entityIds),
    positionId,
    JSON.stringify(caseTypes),
    dateFrom,
    dateTo,
  ].join("|");

  const prevFilterKeyRef = useRef(filterKey);
  useEffect(() => {
    if (prevFilterKeyRef.current === filterKey) return;
    prevFilterKeyRef.current = filterKey;
    setOffset(0);
    setAllRawEvents((prev) => (prev.length === 0 ? prev : []));
  }, [filterKey]);

  // Track offset in a ref so the accumulation effect can read its current
  // value without depending on it — preventing a spurious re-run (and
  // duplicate-append) when offset increments before new data arrives.
  const offsetRef = useRef(offset);
  offsetRef.current = offset;

  const queryResult = useGpComplianceAuditLogQuery({
    fetchPolicy: "cache-and-network",
    variables: {
      arenaId,
      entityIds,
      positionId,
      caseTypes,
      dateFrom,
      dateTo,
      limit,
      offset,
    },
  });

  useEffect(() => {
    const incoming = queryResult.data?.complianceAuditLog?.events;
    if (!incoming?.length) return;
    setAllRawEvents((prev) => (offsetRef.current === 0 ? incoming : [...prev, ...incoming]));
  }, [queryResult.data]);

  const hasMore = queryResult.data?.complianceAuditLog?.hasMore ?? false;
  const isLoadingMore = queryResult.loading && offset > 0;

  const loadMore = useCallback(() => {
    if (!queryResult.loading && hasMore) {
      setOffset((prev) => prev + limit);
    }
  }, [queryResult.loading, hasMore, limit]);

  const events = useMemo(
    () => filterAndTransformGpAuditLogEvents(allRawEvents, arenaName),
    [allRawEvents, arenaName],
  );

  // When the user has no entity access, present the log as empty rather than
  // surfacing the authz error through QueryLoader.
  const visibleQueryResult = isNoEntityAccessError(queryResult.error)
    ? { ...queryResult, error: undefined }
    : queryResult;

  return { events, hasMore, isLoadingMore, loadMore, queryResult: visibleQueryResult };
};
